import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from 'mobx';

import { LoadStateStore } from 'app/loading/LoadStateStore';
import { OrderService } from 'app/service/OrderService';
import { OtpConfirmation } from 'app/model/OtpConfirmation';
import { CardOrder } from 'app/model/CardOrder';

export class SmsSignFormStore {
    @observable
    readonly loadStateStore: LoadStateStore = new LoadStateStore();

    readonly actionStateStore: LoadStateStore = new LoadStateStore(true);

    private readonly orderService: OrderService = new OrderService();

    @observable
    otpConfirmation: OtpConfirmation;

    @observable
    private _otp: string;

    @observable
    order: CardOrder;

    constructor(private orderId: string) {
        makeObservable(this);
    }

    @action
    initialize() {
        return this.loadStateStore.loadData(() => {
            return runInAction(async () => {
                const [order, otpConfirmation] = await Promise.all([
                    this.orderService.getById(this.orderId),
                    this.orderService.getOrderConfirmationOtp(this.orderId),
                ]);

                this.order = order;
                this.otpConfirmation = otpConfirmation;
            });
        });
    }

    @action
    async completeOrder() {
        return this.actionStateStore.loadData(async () => {
            return this.orderService.completeOrder(this.orderId, {
                otp: this._otp,
            });
        });
    }

    /**
     * Возвращает true, если у пользователя есть попытки подтверждения кода otp.
     * false - попытки исчерпаны.
     */
    @computed
    get hasConfirmationAttempts(): boolean {
        return this.otpConfirmation.confirmationCount !== 0;
    }

    /**
     * Возвращает true, если у пользователя есть попытки генерации кода otp
     * false - попытки исчерпаны.
     */
    @computed
    get hasGenerationAttempts(): boolean {
        const generationCount = this.otpConfirmation?.generationCount ?? 0;
        return generationCount !== 0;
    }

    @computed
    get lastConfirmationAttemp(): boolean {
        return this.otpConfirmation.confirmationCount === 1;
    }

    /**
     * Запросить OTP код для подтверждения заявки.
     */
    @action
    async getOrderConfirmationOtp(): Promise<void> {
        return this.actionStateStore.loadData(() => {
            return runInAction(
                async () =>
                    (this.otpConfirmation =
                        await this.orderService.getOrderConfirmationOtp(
                            this.orderId,
                        )),
            );
        });
    }

    @computed
    get generationDelay() {
        return this.otpConfirmation.generationDelay;
    }

    @action
    setOtp = (value: string) => {
        this._otp = value;
    };
}
